import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subject, of, BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { map, share } from 'rxjs/operators';
import { AnnotationPrivateCondition } from '../interfaces';
import { GeneralUtilsService } from './general-utils.service';

export const onAddingAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onEditingAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onExitingEditingAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onEditingCancelAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onSaveEditedAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onAddNewAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onCancelAddingAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onDeleteAnnotation: EventEmitter<any> = new EventEmitter<any>();

export const onAnnotationsLoaded: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationsStateChanged: EventEmitter<any> = new EventEmitter<any>();
export const onOpenAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onCloseActiveAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onReplyCommentAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onDeleteCommentAnnotation: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationFocused: EventEmitter<any> = new EventEmitter<any>();
export const onCommentInputFocused: EventEmitter<any> = new EventEmitter<any>();

export const onPlayerReady: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlayerInitializing: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlayerInitialized: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlayerLoadedData: EventEmitter<any> = new EventEmitter<any>();
export const onAudioPlayerInitialized: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlayerErrorLoaded: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlayerDisposed: EventEmitter<any> = new EventEmitter<any>();
export const onAddingAnnotationDataChanged: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPaused: EventEmitter<any> = new EventEmitter<any>();
export const onVideoPlay: EventEmitter<any> = new EventEmitter<any>();

export const onImageInitReady: EventEmitter<any> = new EventEmitter<any>();
export const onImageZoomChanged: EventEmitter<any> = new EventEmitter<any>();
export const onImageViewportChanged: EventEmitter<any> = new EventEmitter<any>();
export const onImageInitialResize: EventEmitter<any> = new EventEmitter<any>();
export const onImageResetOrientation: EventEmitter<any> = new EventEmitter<any>();

export const onFirstRowMarkersReady: EventEmitter<any> = new EventEmitter<any>();

export const onPluginReady: EventEmitter<any> = new EventEmitter<any>();

export const onAnnotationHoverOpened: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationHoverClosed: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationClickOpened: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationClickClosed: EventEmitter<any> = new EventEmitter<any>();

export const onAnnotationMarkerSelectedActiveHover: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationMarkerSelectedInActiveHover: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationMarkerSelectedActiveClick: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationMarkerSelectedInActiveClick: EventEmitter<any> = new EventEmitter<any>();

//Audio Hover/Click Events
export const onAnnotationHoveredIn: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationHoveredOut: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationClickedIn: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationClickedOut: EventEmitter<any> = new EventEmitter<any>();

// V2 Events to combine the click evens of all different type assets, currently they use different events for each type
// TODO: Remove the old events after phasing out the old comment UI. They're used only in the new comment UI currently
export const onAnnotationClickOpenedV2: EventEmitter<any> = new EventEmitter<any>();
export const onAnnotationClickClosedV2: EventEmitter<any> = new EventEmitter<any>();

export const onUserColourCreated: EventEmitter<any> = new EventEmitter<any>();

export const onShowAnnotationCommentReplies: EventEmitter<any> = new EventEmitter<any>();

export const onTimeFormatChange: EventEmitter<any> = new EventEmitter<any>();
export const onTimeFormatChangePlugin: EventEmitter<any> = new EventEmitter<any>();

export const wsAnnotationChange: EventEmitter<any> = new EventEmitter<any>();

export const onMediaDownloadClick: EventEmitter<any> = new EventEmitter<any>();
export const onCommentsVisibilityChange: EventEmitter<any> = new EventEmitter<any>();

export const onConversationPluginReady: EventEmitter<any> = new EventEmitter<any>();

export const onAnnotationDirtyStateChanged: Subject<{ dirty: boolean; instanceId: string }> = new Subject();

export class AnnotationComment {
  id: string;
  body: string;
  meta: {
    datetime: any;
    updatetime: any;
    user_id: string;
    user_name: string;
  };
  parentId: string;
  files: any[];
}

export class AnnotationCommentDTO {
  id: string;
  body: string;
  files: any;
  taggedUsers: any;
  meta: {
    datetime: any;
    updatetime: any;
    username: string;
  };
  parentId: string;
}

export class Annotation {
  id: string;
  shape: string;
  range?: { start: number; end: number; isGeneralComment?: boolean };
  resolution: { height: number; width: number };
  comments: Array<AnnotationComment>;

  //optional properties
  color?: string;
  colorOpacity?: string;
  isCommentInTimeRange?: boolean;
  rangeInTimeString?: { start: number | null; end: number | null };
  secondRangeString?: { start: number; end: number };
  isPublic?: boolean;
}

export class AnnotationDTO {
  id?: string;
  shape: string;
  resolution: string;
  range?: string;
  privateConditionKey?: AnnotationPrivateCondition;
  entityType: string;
  comments: Array<AnnotationCommentDTO>;
}

export type MediaAnnotationQueryParams = {
  projectId: string;
  sectionId: string;
  subsectionId: string;
  typeId: string;
  itemId: string;
  participants: any[];
  privateConditionRequest?: AnnotationPrivateCondition;
  nativeObject?: boolean;
  cacheRequest?: boolean;
  lightweight?: boolean;
};

export type AnnotationStateChangedParams = {
  args: string;
  data: AnnotationDTO[];
  projectId: string;
  sectionId: string;
  subSectionId: string;
  typeId: string;
  itemId: string;
  instanceId?: string;
};

export enum AnnotationCountEntityTypes {
  Subsection = 'SUBSECTION',
  Folder = 'FOLDER',
  Subfolder = 'SUBFOLDER',
  Version = 'VERSION',
  FileAndPage = 'FILE_AND_PAGE', //total file + its pages comment count
  File = 'FILE',
  Page = 'PAGE',
  PagesInFile = 'PAGES_IN_FILE',
}

export interface AnnotationCountParam {
  id: string;
  entityType: AnnotationCountEntityTypes;
  versionNumber?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AnnotationService {
  private readonly cache: any = {};
  private readonly cachedObservable: any = {};
  private commentListVisibility: AnnotationPrivateCondition = null;
  private privateCondition: AnnotationPrivateCondition = null;

  //for editor
  privateConditionChange$: BehaviorSubject<AnnotationPrivateCondition> = new BehaviorSubject(this.privateCondition);
  //same stuff, but for comment list
  commentsVisibilityChange$: BehaviorSubject<AnnotationPrivateCondition> = new BehaviorSubject(
    this.commentListVisibility
  );

  constructor(
    private readonly apiService: ApiService,
    private readonly generalUtilsService: GeneralUtilsService
  ) {}

  getCommentViewModel(comment: any, participants: any[] = []) {
    const participantsList = participants ?? [];
    const annotationComment = new AnnotationComment();
    annotationComment.id = comment.id;
    annotationComment.body = comment.body;
    annotationComment.parentId = comment.parentId;
    annotationComment.files = comment.files;
    annotationComment.meta = {
      datetime: comment.meta.datetime,
      updatetime: comment.meta.updatetime,
      user_id: comment.meta.username,
      user_name: participantsList.find((p) => p.username === comment.meta.username)?.fullName || '',
    };
    annotationComment.parentId = comment.parentId;
    return annotationComment;
  }

  getAnnotationViewModel(annotation: any, participants: any[] = []) {
    const participantsList = participants ?? [];
    const range = JSON.parse(annotation.range);
    const resolution = JSON.parse(annotation.resolution) || {};
    const entity = new Annotation();
    entity.id = annotation.id;
    if (range) {
      entity.range = {
        start: parseFloat(range.start || 0),
        end: parseFloat(range.end || 0),
        isGeneralComment: range.isGeneralComment || false,
      };
    } else {
      entity.range = range;
    }
    entity.resolution = {
      height: parseFloat(resolution.height),
      width: parseFloat(resolution.width),
    };
    entity.shape = annotation.shape;
    entity.comments = new Array<AnnotationComment>();
    entity.comments = annotation.comments.map((comment: any) => {
      const annotationComment = new AnnotationComment();
      annotationComment.id = comment.id;
      annotationComment.body = comment.body;
      annotationComment.parentId = comment.parentId;
      annotationComment.files = comment.files;
      annotationComment.meta = {
        datetime: comment.meta.datetime,
        updatetime: comment.meta.updatetime,
        user_id: comment.meta.username,
        user_name: participantsList.find((p) => p.username === comment.meta.username)?.fullName || '',
      };
      return annotationComment;
    });
    return entity;
  }

  getMediaAnnotations(params: MediaAnnotationQueryParams): Observable<any> {
    const {
      projectId,
      sectionId,
      subsectionId,
      typeId,
      itemId,
      participants,
      nativeObject = false,
      lightweight = false,
      privateConditionRequest = false,
    } = params;
    const url = `/api/mediaAnnotations/${projectId}/${sectionId}/${subsectionId}/${typeId}/${itemId}`;
    //const url = `/api/mediaAnnotations/${projectId}/${sectionId}/${subsectionId}/${itemId}/${filePageId}`;
    const queryParams: string[] = [];
    if (lightweight) {
      queryParams.push(`lightweight=true`);
    }
    if (privateConditionRequest && privateConditionRequest !== 'ALL') {
      queryParams.push(`privateConditionRequest=${privateConditionRequest}`);
    }
    const queryParamsString: string = queryParams.length ? '?' + queryParams.join('&') : '';
    return this.apiService.httpGet(url + queryParamsString).pipe(
      share(),
      map((data: any) => {
        let result: any = null;
        if (nativeObject) {
          result = data;
        } else {
          const annotations = new Array<Annotation>();
          data.map((annotation: any) => {
            const parents = annotation.comments.filter((comment: any) =>
              this.generalUtilsService.isObjectEmpty(comment.parentId)
            );
            const replies = annotation.comments.filter(
              (comment: any) => !this.generalUtilsService.isObjectEmpty(comment.parentId)
            );
            replies.sort((commentA: any, commentB: any) => {
              return (commentA.meta?.datetime ?? 0) - (commentB.meta?.datetime ?? 0);
            });
            annotation.comments = [...parents, ...replies];
            const entity = this.getAnnotationViewModel(annotation, participants);
            annotations.push(entity);
          });
          annotations.sort((annotationA: any, annotationB: any) => {
            const dateB = new Date(annotationB.comments?.[0]?.meta?.datetime ?? 0).getTime();
            const dateA = new Date(annotationA.comments?.[0]?.meta?.datetime ?? 0).getTime();
            return dateB - dateA;
          });
          result = annotations;
        }
        return result;
      })
    );
  }

  addNewAnnotation(
    projectId: string,
    sectionId: string,
    subSectionId: string,
    typeId: string,
    itemId: string,
    annotation: any,
    participants: any[],
    entityType = 'ASSET'
  ): Observable<any> {
    const subject = new Subject<any>();
    const payload = new AnnotationDTO();
    payload.id = annotation.id;
    payload.range = JSON.stringify(annotation.range);
    payload.resolution = JSON.stringify(annotation.resolution);
    payload.shape = annotation.shape;
    payload.entityType = entityType;
    if (annotation.privateConditionKey) {
      payload.privateConditionKey = annotation.privateConditionKey;
    }
    payload.comments = new Array<AnnotationCommentDTO>();

    const firstCommentId = annotation.comments[0]?.id;
    annotation.comments.forEach((comment: any) => {
      const { id, body, files, parentId, meta } = comment;
      const annotationCommentDTO = new AnnotationCommentDTO();
      annotationCommentDTO.id = id;
      annotationCommentDTO.body = body;
      annotationCommentDTO.files = files;
      annotationCommentDTO.parentId = id === firstCommentId ? undefined : parentId;
      annotationCommentDTO.meta = {
        datetime: meta.datetime,
        updatetime: meta.updatetime,
        username: meta.user_id,
      };
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
      const taggedUsers = body.match(emailRegex) || [];
      annotationCommentDTO.taggedUsers = [...new Set(taggedUsers)];
      payload.comments.push(annotationCommentDTO);
    });
    this.apiService
      .httpPut(`/api/mediaAnnotations/${projectId}/${sectionId}/${subSectionId}/${typeId}/${itemId}`, payload)
      .subscribe(
        (data) => {
          const annotations = this.getAnnotationViewModel(data, participants);
          subject.next(annotations);
        },
        (err) => {
          subject.error(err);
        }
      );
    return subject.asObservable();
  }

  deleteAnnotation(
    projectId: string,
    sectionId: string,
    subSectionId: string,
    typeId: string,
    itemId: string,
    annotationId: string
  ) {
    const subject = new Subject<any>();
    this.apiService
      .httpDelete(`/api/mediaAnnotations/${projectId}/${sectionId}/${subSectionId}/${typeId}/${itemId}/${annotationId}`)
      .subscribe(
        (data) => {
          subject.next(data);
        },
        (err) => {
          subject.error(err);
        }
      );
    return subject.asObservable();
  }

  deleteAnnotationComment(
    projectId: string,
    sectionId: string,
    subSectionId: string,
    typeId: string,
    itemId: string,
    annotationId: string,
    commentId: string
  ) {
    const subject = new Subject<any>();
    this.apiService
      .httpDelete(
        `/api/mediaAnnotations/${projectId}/${sectionId}/${subSectionId}/${typeId}/${itemId}/${annotationId}/${commentId}`
      )
      .subscribe(
        (data) => {
          subject.next(data);
        },
        (err) => {
          subject.error(err);
        }
      );
    return subject.asObservable();
  }

  updatePrivateCondition(value: AnnotationPrivateCondition) {
    if (value === this.privateCondition) {
      return;
    }
    this.privateCondition = value;
    this.privateConditionChange$.next(value);
  }

  updateCommentListVisibility(value: AnnotationPrivateCondition) {
    if (value === this.commentListVisibility) {
      return;
    }
    this.commentListVisibility = value;
    this.commentsVisibilityChange$.next(value);
  }
}
